import { useAtom, useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { classNames } from '@/utils/classNames';
import getIGUsernameTags, { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';

import type { MessageType } from '@/types/messageType';
import type {
    DMConversation,
    MentionType,
    SocialInboxMessage as SocialInboxMessageType
} from '@/components/pages/conversations/SocialInbox/types';

import { CAPTION, TAG } from '@/lib/manage/mentions';
import { INSTAGRAM } from '@/lib/socialProfile';
import {
    INSTAGRAM_ONLY_FEATURES,
    SOCIAL_INBOX_META
} from '@/components/pages/conversations/SocialInbox/const';

import {
    conversationParticipantProfilePicturesAtom,
    openedMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

import { DefaultAvatar } from '@/components/base/Avatar';
import Comment from '@/components/base/Comment';
import Icon from '@/components/base/Icon';
import Mention from '@/components/base/Mention';
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';
import { MessageDate } from '@/components/pages/conversations/SocialInbox/components/MessageDate';
import { SocialInboxDirectMessageActions } from '@/components/pages/conversations/SocialInbox/components/SocialInboxMessageList/SocialInboxMessage/SocialInboxDirectMessageActions/SocialInboxDirectMessageActions';

interface SocialInboxMessageProps {
    message?: SocialInboxMessageType;
    onPostClick?: (mediaId: string) => void;
}

export const SocialInboxMessage = ({ message, onPostClick }: SocialInboxMessageProps) => {
    const { currentSocialInbox } = useSocialInboxNavigation();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom);
    const conversationParticipantProfilePictures = useAtomValue(
        conversationParticipantProfilePicturesAtom
    );
    // Important and Resolved are not social inbox types
    const socialInboxType = INSTAGRAM_ONLY_FEATURES.includes(
        currentSocialInbox as (typeof INSTAGRAM_ONLY_FEATURES)[number]
    )
        ? currentSocialInbox
        : SOCIAL_INBOX_META.comment.key;

    const isOpen = openedMessage && message.id === openedMessage.id;
    const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
    const isDirectMessage = currentSocialInbox === SOCIAL_INBOX_META.dm.key;
    const isRead = 'is_read' in message ? message.is_read : message.seen_at;

    const onOpen = () => {
        setOpenedMessage(message);
    };

    const handlePostClick = () => {
        if (!('media' in message) || !message.media?.id) return;

        onPostClick?.(message.media.id);
    };

    /* Widget */

    const renderUsername = () => {
        const getInstagramUsername = () => {
            if (socialInboxType === SOCIAL_INBOX_META.mention.key) {
                const mention = message as MentionType;
                // In the case of a mention in a comment, we need to go look for the username in the comment object
                if (mention.comment && 'data' in mention.comment) {
                    return mention.comment.data.username;
                }
                // In the case of a mention in a post, we need to go look for the username in the post object
                if (mention.media && 'data' in mention.media) {
                    return mention.media.data.username;
                }
            }
            if ('data' in message) {
                return message.data.username;
            }
            return null;
        };

        if ('data' in message && !message.data?.username) return;

        if (isInstagram) {
            if ('last_message' in message) {
                return (
                    <span className="font-GSemiBold text-icoBlack dark:text-white">
                        {message.last_message?.is_from_me
                            ? message.recipient.name
                            : message.initiator.name}
                    </span>
                );
            }

            return (
                <a
                    href={getIGUsernameLink(getInstagramUsername() ?? '')}
                    className={classNames('text-sm hover:text-icoBlue', {
                        'text-icoBlue': !message?.is_read
                    })}
                    target="_blank"
                    rel="noopener noreferrer">
                    {getInstagramUsername()}
                </a>
            );
        }

        return (
            <span className="font-GSemiBold text-icoBlack">
                {(message as MessageType)?.data?.username}
            </span>
        );
    };

    const renderMentionMessage = (mention) => {
        if (mention.mentioned_in === TAG) {
            return <Mention.TagMessage username={mention.media?.username} />;
        }

        let message =
            mention.comment?.data?.parent?.message ?? mention.comment?.data?.message ?? '';

        if (mention.mentioned_in === CAPTION) {
            message = mention.media?.caption
                ?.replaceAll(
                    new RegExp(`@${activeSocialProfile.slug}`, 'g'),
                    activeSocialProfile.slug
                )
                ?.replaceAll(
                    new RegExp(activeSocialProfile.slug, 'g'),
                    `@${activeSocialProfile.slug}`
                );
        }

        const tags = getIGUsernameTags(message);

        return (
            <Comment.Message
                awsTranslation={undefined}
                message={message}
                tags={tags}
                className="text-xs"
            />
        );
    };

    const renderMessage = () => {
        if ('mentioned_in' in message) {
            return renderMentionMessage(message);
        }
        if ('last_message' in message) {
            return (
                <span className="font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {message?.last_message?.content}
                </span>
            );
        }
        return (
            <span className="font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                {message?.data?.message}
            </span>
        );
    };

    return (
        <div className="group relative">
            {!isRead && (
                <div className="absolute size-3 -translate-x-1/4 -translate-y-1/4 rounded-full border-2 border-white bg-icoBlue dark:border-icoDarkMode-deepBlack" />
            )}
            <button
                className={classNames(
                    'flex h-20 w-full overflow-hidden rounded border-[1px]',
                    isOpen
                        ? 'border-2 border-icoBlue'
                        : 'border-icoGray-300 dark:border-icoDarkMode-beton'
                )}
                onClick={onOpen}
                key={message.id}>
                <div
                    className={classNames(
                        'flex items-center justify-center px-1',
                        isRead
                            ? 'bg-icoGray-100 dark:bg-icoDarkMode-anthracite'
                            : 'bg-icoBlue-100 dark:bg-icoBlue/20'
                    )}>
                    {activeSocialProfile && (
                        <div className="flex flex-col items-center space-y-1">
                            <ProfileItem size="sm" profile={activeSocialProfile} />
                            <span className="w-16 truncate font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {activeSocialProfile.slug}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex h-full grow items-center space-x-3 p-3">
                    {'media' in message && (
                        <div className="relative aspect-1 h-full shrink-0 overflow-hidden rounded-lg">
                            <Comment.Post
                                media={message.media}
                                onClick={handlePostClick}
                                displayPost
                                remoteThumbnail
                            />
                        </div>
                    )}

                    <div className="flex h-full grow flex-col overflow-hidden">
                        <div className="relative flex w-full items-center justify-between pb-1">
                            <div className="flex items-center space-x-1">
                                {'initiator' in message && (
                                    <a
                                        href={getIGUsernameLink(message.initiator.name)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                        className="flex items-center gap-1.5">
                                        <DefaultAvatar
                                            size="xs"
                                            url={
                                                conversationParticipantProfilePictures[
                                                    message.initiator.meta_participant_id
                                                ]
                                            }
                                            fallback={DirectMessageService.getUserInitials(
                                                message.initiator.name
                                            )}
                                        />

                                        <span className="font-GSemiBold text-xs text-icoBlack hover:text-icoBlue dark:text-white">
                                            {message.initiator.name}
                                        </span>
                                    </a>
                                )}
                                <Icon
                                    icon={SOCIAL_INBOX_META[socialInboxType].icon}
                                    className="text-13 text-icoBlue"
                                />

                                <MessageDate
                                    date={
                                        'created' in message
                                            ? message.created
                                            : message.last_message?.sent_at
                                    }
                                    className="font-GMedium text-xxs tracking-wider text-icoGray-500"
                                />
                            </div>

                            {isDirectMessage && (
                                <SocialInboxDirectMessageActions
                                    message={message as DMConversation}
                                />
                            )}
                        </div>
                        <span className="grow space-x-1 overflow-hidden break-all text-left text-sm">
                            {renderUsername()}

                            {renderMessage()}
                        </span>
                    </div>
                </div>
            </button>
        </div>
    );
};
